import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import Marquee from "react-fast-marquee";
import { api } from "@/lib/api";
import ItemCard from "@/components/ItemCard";
import { ArrowRight, Crown, MicrophoneStage, Sparkle, Stack, DownloadSimple } from "@phosphor-icons/react";

const stagger = { show: { transition: { staggerChildren: 0.08 } } };
const fadeUp = { hidden: { opacity: 0, y: 24 }, show: { opacity: 1, y: 0, transition: { duration: 0.5 } } };

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const [comedy, setComedy] = useState([]);
  const [bundles, setBundles] = useState([]);

  useEffect(() => {
    api.get("/items", { params: { featured: true } }).then((r) => setFeatured(r.data.slice(0, 5)));
    api.get("/comedy").then((r) => setComedy(r.data.slice(0, 3)));
    api.get("/bundles", { params: { featured: true } }).then((r) => setBundles(r.data.slice(0, 2)));
  }, []);

  return (
    <div className="grid-bg">
      {/* Hero */}
      <section className="relative max-w-[1400px] mx-auto px-5 lg:px-8 pt-20 pb-24 lg:pt-28">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <motion.div initial="hidden" animate="show" variants={stagger} className="lg:col-span-7">
            <motion.div variants={fadeUp} className="inline-flex items-center gap-2 glass px-4 py-2 rounded-full mb-8">
              <MicrophoneStage weight="fill" size={16} className="text-[#FF00AA]" />
              <span className="text-xs font-medium uppercase tracking-[0.2em] text-white/70">Themes &amp; Things</span>
            </motion.div>
            <motion.h1 variants={fadeUp} className="font-display font-black text-4xl sm:text-6xl lg:text-7xl tracking-tighter leading-[0.95] uppercase">
              Your home screen<br />
              deserves a <span className="text-[#FFEB3B] text-glow-yellow">punchline</span>
            </motion.h1>
            <motion.p variants={fadeUp} className="text-white/60 text-lg mt-8 max-w-xl leading-relaxed">
              Aesthetic app icon packs, glowing clock &amp; calendar widgets, and matching device wallpapers —
              curated by D&amp;J's Comedy Club. Serious design, unserious energy.
            </motion.p>
            <motion.div variants={fadeUp} className="flex flex-wrap gap-4 mt-10">
              <Link to="/marketplace" data-testid="hero-browse-btn"
                className="group flex items-center gap-2 bg-[#FFEB3B] text-[#05050A] font-bold px-7 py-4 rounded-sm hover:bg-[#fff35c] hover:shadow-[0_0_28px_rgba(255,235,59,0.5)] transition-all">
                Browse the drop
                <ArrowRight weight="bold" size={18} className="group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link to="/premium" data-testid="hero-premium-btn"
                className="flex items-center gap-2 glass border border-white/20 px-7 py-4 rounded-sm font-bold hover:border-[#00E5FF] hover:text-[#00E5FF] transition-colors">
                <Crown weight="fill" size={18} /> Go Premium
              </Link>
            </motion.div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7 }}
            className="lg:col-span-5 relative">
            <div className="absolute -inset-8 bg-[#00E5FF]/10 blur-3xl rounded-full" />
            <img
              src="https://images.unsplash.com/photo-1738641928021-15dedad586da?crop=entropy&cs=srgb&fm=jpg&q=85&w=900"
              alt="Custom themed phone"
              className="relative rounded-2xl border border-white/10 animate-float w-full object-cover aspect-[4/5]"
            />
          </motion.div>
        </div>
      </section>

      {/* Marquee */}
      <div className="border-y border-white/10 py-5 bg-[#0A0A14]">
        <Marquee speed={60} gradient={false}>
          {["ICON PACKS", "•", "CLOCK WIDGETS", "•", "CALENDAR WIDGETS", "•", "WALLPAPERS", "•", "COMEDY BITS", "•", "PREMIUM UNLIMITED", "•"].map((t, i) => (
            <span key={`marquee-${i}-${t}`} className="font-display font-black text-2xl uppercase tracking-tighter mx-6 text-white/80">
              {t === "•" ? <span className="text-[#FF00AA]">✦</span> : t}
            </span>
          ))}
        </Marquee>
      </div>

      {/* Featured bento */}
      <section className="max-w-[1400px] mx-auto px-5 lg:px-8 py-24">
        <div className="flex items-end justify-between mb-12">
          <div>
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#00E5FF]">Featured Drop</span>
            <h2 className="font-display font-black text-3xl lg:text-4xl tracking-tighter uppercase mt-3">Fresh off the stage</h2>
          </div>
          <Link to="/marketplace" className="hidden sm:flex items-center gap-2 text-white/60 hover:text-[#00E5FF] transition-colors font-medium">
            View all <ArrowRight size={16} />
          </Link>
        </div>
        {featured.length > 0 && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
            <div className="col-span-2 lg:row-span-2">
              <ItemCard item={featured[0]} className="h-full" />
            </div>
            {featured.slice(1, 5).map((it) => (
              <ItemCard key={it.id} item={it} />
            ))}
          </div>
        )}
      </section>

      {/* Featured bundles */}
      {bundles.length > 0 && (
        <section className="max-w-[1400px] mx-auto px-5 lg:px-8 pb-8">
          <div className="flex items-end justify-between mb-12">
            <div>
              <span className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-[#FFEB3B]">
                <Stack weight="fill" size={16} /> Theme Bundles
              </span>
              <h2 className="font-display font-black text-3xl lg:text-4xl tracking-tighter uppercase mt-3">The whole look, one tap</h2>
            </div>
            <Link to="/bundles" className="hidden sm:flex items-center gap-2 text-white/60 hover:text-[#FFEB3B] transition-colors font-medium">
              All bundles <ArrowRight size={16} />
            </Link>
          </div>
          <div className="grid md:grid-cols-2 gap-6" data-testid="home-featured-bundles">
            {bundles.map((b) => (
              <Link key={b.id} to={`/bundle/${b.id}`} data-testid={`home-bundle-${b.id}`}
                className="group relative flex overflow-hidden rounded-2xl glass hover:border-[#FFEB3B]/50 hover:-translate-y-1 transition-all duration-300">
                <div className="relative w-40 shrink-0 overflow-hidden">
                  <img src={b.cover_image} alt={b.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
                <div className="p-6 flex-1 min-w-0">
                  <span className="inline-flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest text-[#05050A] bg-[#FFEB3B] px-2 py-0.5 rounded-sm">
                    <Stack weight="fill" size={11} /> {b.item_ids.length}-piece
                  </span>
                  <h3 className="font-display font-black text-xl tracking-tighter uppercase mt-3 group-hover:text-[#FFEB3B] transition-colors truncate">{b.title}</h3>
                  <p className="text-white/50 text-sm mt-2 line-clamp-2">{b.description}</p>
                  <span className="flex items-center gap-1 text-white/40 text-xs mt-3"><DownloadSimple size={13} /> {b.download_count} downloads</span>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}
      <section className="relative py-24 overflow-hidden">
        <img src="https://images.unsplash.com/photo-1648237409808-aa4649c07ec8?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600"
          alt="" className="absolute inset-0 w-full h-full object-cover opacity-20" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#05050A] via-[#05050A]/80 to-[#05050A]" />
        <div className="relative max-w-[1400px] mx-auto px-5 lg:px-8">
          <div className="flex items-center gap-3 mb-4">
            <Sparkle weight="fill" size={20} className="text-[#FF00AA]" />
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#FF00AA]">From the Green Room</span>
          </div>
          <h2 className="font-display font-black text-3xl lg:text-5xl tracking-tighter uppercase mb-12 max-w-2xl">
            Jokes that pair with your wallpaper
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            {comedy.map((c) => (
              <div key={c.id} className="glass rounded-lg p-7 hover:border-[#FF00AA]/50 transition-colors">
                <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#FF00AA]">{c.category}</span>
                <p className="text-white/80 leading-relaxed mt-4 text-[15px]">"{c.content}"</p>
                <p className="text-white/40 text-sm mt-5 font-display">— {c.author}</p>
              </div>
            ))}
          </div>
          <Link to="/comedy" className="inline-flex items-center gap-2 mt-10 text-[#FF00AA] font-bold hover:gap-3 transition-all">
            More bits <ArrowRight size={16} />
          </Link>
        </div>
      </section>

      {/* Premium CTA */}
      <section className="max-w-[1400px] mx-auto px-5 lg:px-8 pb-8">
        <div className="relative overflow-hidden rounded-2xl glass p-10 lg:p-16">
          <div className="absolute -right-20 -top-20 w-80 h-80 bg-[#FFEB3B]/20 blur-[100px] rounded-full" />
          <div className="relative max-w-2xl">
            <Crown weight="fill" size={40} className="text-[#FFEB3B] mb-6" />
            <h2 className="font-display font-black text-3xl lg:text-5xl tracking-tighter uppercase">
              Unlimited downloads.<br />One <span className="text-[#FFEB3B]">premium</span> pass.
            </h2>
            <p className="text-white/60 text-lg mt-6">
              Every icon pack, every widget, every wallpaper — download as many as you want. No punchline, just perks.
            </p>
            <Link to="/premium" data-testid="cta-premium-btn"
              className="inline-flex items-center gap-2 bg-[#FFEB3B] text-[#05050A] font-bold px-7 py-4 rounded-sm mt-8 hover:bg-[#fff35c] hover:shadow-[0_0_28px_rgba(255,235,59,0.5)] transition-all">
              See the plans <ArrowRight weight="bold" size={18} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
